import React, { useState, useEffect } from 'react';
import { FiMove, FiEdit2, FiSave, FiX, FiTrash2 } from 'react-icons/fi';
import { apiService } from '../services/api';
import Toast, { ToastType } from './Toast';

interface Feature {
  id: number;
  key: string;
  title_en: string;
  title_fr: string;
  value_type: 'boolean' | 'integer' | 'text';
  is_active: boolean;
  is_new: boolean;
  is_static: boolean;
  display_order: number;
}

interface FeatureWithValue extends Feature {
  value: any;
}

interface DraggableFeatureListProps {
  packageId?: number;
  packageFeatures: Record<number | string, any>; // Accepts both number IDs (dynamic) and string keys (static)
  onFeaturesChange?: (features: FeatureWithValue[]) => void;
}

const DraggableFeatureList: React.FC<DraggableFeatureListProps> = ({
  packageId,
  packageFeatures,
  onFeaturesChange,
}) => {
  const [features, setFeatures] = useState<FeatureWithValue[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [originalOrder, setOriginalOrder] = useState<number[]>([]); // Track original order before drag
  const [editingFeature, setEditingFeature] = useState<number | null>(null);
  const [editedTitles, setEditedTitles] = useState<{ title_en: string; title_fr: string }>({ title_en: '', title_fr: '' });
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ featureId: number; featureName: string } | null>(null);

  // Protected system features that cannot be deleted
  const PROTECTED_FEATURES = ['interview_tips', 'job_auto_fill', 'settings'];

  useEffect(() => {
    fetchFeatures();
  }, [packageId, packageFeatures]);

  const fetchFeatures = async () => {
    try {
      const response = await apiService.getFeatures();
      if (response.success) {
        const featuresWithValues = response.data.map((feature: Feature) => {
          // For static features, use key (string) to get value; for dynamic, use id (number)
          const lookupKey = feature.is_static ? feature.key : feature.id;
          return {
            ...feature,
            value: packageFeatures[lookupKey] !== undefined ? packageFeatures[lookupKey] : getDefaultValue(feature),
          };
        });
        setFeatures(featuresWithValues.sort((a, b) => a.display_order - b.display_order));
      }
    } catch (error) {
      console.error('Error fetching features:', error);
    }
  };

  const getDefaultValue = (feature: Feature) => {
    if (feature.value_type === 'boolean') return false;
    if (feature.value_type === 'integer') return 0;
    return '';
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
    // Save current order to compare later
    setOriginalOrder(features.map(f => f.id));
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newFeatures = [...features];
    const draggedFeature = newFeatures[draggedIndex];
    newFeatures.splice(draggedIndex, 1);
    newFeatures.splice(index, 0, draggedFeature);

    setFeatures(newFeatures);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null) return;

    // Check if order actually changed
    const currentOrder = features.map(f => f.id);
    const orderChanged = JSON.stringify(currentOrder) !== JSON.stringify(originalOrder);

    // Reset drag state
    setDraggedIndex(null);
    setOriginalOrder([]);

    // Only update if order actually changed
    if (!orderChanged) {
      return;
    }

    // Update display_order for all features
    const updatedFeatures = features.map((feature, index) => ({
      id: feature.id,
      display_order: index + 1,
    }));

    try {
      setLoading(true);
      const response = await apiService.bulkUpdateFeatureOrder(updatedFeatures);
      if (response.success) {
        setToast({ message: 'Feature order updated successfully', type: 'success' });
        // Update local state with new order
        const reorderedFeatures = features.map((f, idx) => ({ ...f, display_order: idx + 1 }));
        setFeatures(reorderedFeatures);
        if (onFeaturesChange) {
          onFeaturesChange(reorderedFeatures);
        }
      }
    } catch (error: any) {
      setToast({ message: `Error updating order: ${error.message}`, type: 'error' });
      fetchFeatures(); // Reload original order
    } finally {
      setLoading(false);
    }
  };

  const handleEditFeature = (feature: Feature) => {
    setEditingFeature(feature.id);
    setEditedTitles({
      title_en: feature.title_en,
      title_fr: feature.title_fr,
    });
  };

  const handleSaveFeature = async (featureId: number) => {
    try {
      const response = await apiService.updateFeature(featureId, editedTitles);
      if (response.success) {
        setToast({ message: 'Feature titles updated successfully', type: 'success' });
        setEditingFeature(null);
        fetchFeatures();
      }
    } catch (error: any) {
      setToast({ message: `Error updating feature: ${error.message}`, type: 'error' });
    }
  };

  const handleCancelEdit = () => {
    setEditingFeature(null);
    setEditedTitles({ title_en: '', title_fr: '' });
  };

  const handleValueChange = (featureId: number, newValue: any) => {
    const updatedFeatures = features.map((f) =>
      f.id === featureId ? { ...f, value: newValue } : f
    );
    setFeatures(updatedFeatures);
    if (onFeaturesChange) {
      onFeaturesChange(updatedFeatures);
    }
  };

  const handleDeleteFeature = (feature: Feature) => {
    // Check if feature is protected
    if (feature.is_static) {
      setToast({ message: 'Static features cannot be deleted', type: 'error' });
      return;
    }
    
    if (PROTECTED_FEATURES.includes(feature.key)) {
      setToast({ message: 'This system feature cannot be deleted', type: 'error' });
      return;
    }

    // Show confirmation dialog
    setDeleteConfirm({
      featureId: feature.id,
      featureName: feature.title_en
    });
  };

  const confirmDeleteFeature = async () => {
    if (!deleteConfirm) return;

    try {
      setLoading(true);
      const response = await apiService.deleteFeature(deleteConfirm.featureId);
      if (response.success) {
        setToast({ message: 'Feature deleted successfully', type: 'success' });
        fetchFeatures();
      }
    } catch (error: any) {
      setToast({ message: `Error deleting feature: ${error.message}`, type: 'error' });
    } finally {
      setLoading(false);
      setDeleteConfirm(null);
    }
  };

  const canDeleteFeature = (feature: Feature): boolean => {
    return !feature.is_static && !PROTECTED_FEATURES.includes(feature.key);
  };

  const renderFeatureValue = (feature: FeatureWithValue) => {
    // Both static and dynamic features are editable inline
    if (feature.value_type === 'boolean') {
      return (
        <label className="flex items-center cursor-pointer">
          <div className="relative">
            <input
              type="checkbox"
              checked={feature.value}
              onChange={(e) => handleValueChange(feature.id, e.target.checked)}
              className="sr-only"
            />
            <div className={`block w-10 h-6 rounded-full ${feature.value ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
            <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition ${feature.value ? 'transform translate-x-4' : ''}`}></div>
          </div>
        </label>
      );
    }

    if (feature.value_type === 'integer') {
      return (
        <input
          type="text"
          value={feature.value || ''}
          onChange={(e) => {
            const val = e.target.value;
            // Allow empty, minus sign, or valid numbers
            if (val === '' || val === '-' || /^-?\d+$/.test(val)) {
              handleValueChange(feature.id, val === '' ? '' : parseInt(val) || 0);
            }
          }}
          className="w-24 rounded border border-gray-300 px-2 py-1 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
          placeholder={feature.is_static ? "e.g., -1" : ""}
        />
      );
    }

    return (
      <input
        type="text"
        value={feature.value}
        onChange={(e) => handleValueChange(feature.id, e.target.value)}
        className="w-48 rounded border border-gray-300 px-2 py-1 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
      />
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Package Features {loading && <span className="text-sm text-gray-500">(Updating...)</span>}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Drag to reorder • Click edit to change titles
        </p>
      </div>

      {features.map((feature, index) => (
        <div
          key={feature.id}
          draggable
          onDragStart={() => handleDragStart(index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragEnd={handleDragEnd}
          className={`flex items-center justify-between rounded-lg border bg-white p-4 transition dark:bg-navy-800 ${
            draggedIndex === index
              ? 'border-blue-500 shadow-lg'
              : 'border-gray-200 dark:border-navy-600'
          }`}
        >
          <div className="flex items-center space-x-3 flex-1">
            <FiMove className="h-5 w-5 cursor-move text-gray-400" />
            
            {editingFeature === feature.id ? (
              <div className="flex-1 space-y-2">
                <input
                  type="text"
                  value={editedTitles.title_en}
                  onChange={(e) => setEditedTitles({ ...editedTitles, title_en: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveFeature(feature.id);
                    }
                  }}
                  placeholder="English Title"
                  className="w-full rounded border border-gray-300 px-3 py-1 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
                <input
                  type="text"
                  value={editedTitles.title_fr}
                  onChange={(e) => setEditedTitles({ ...editedTitles, title_fr: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveFeature(feature.id);
                    }
                  }}
                  placeholder="French Title"
                  className="w-full rounded border border-gray-300 px-3 py-1 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
            ) : (
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900 dark:text-white">{feature.title_en}</p>
                  {!feature.is_active && (
                    <span className="bg-red-100 text-red-700 px-2 py-0.5 text-xs rounded-full font-medium dark:bg-red-900/30 dark:text-red-400">
                      INACTIVE
                    </span>
                  )}
                  {feature.is_new && (
                    <span className="bg-green-100 text-green-700 px-2 py-0.5 text-xs rounded-full font-medium dark:bg-green-900/30 dark:text-green-400">
                      NEW
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{feature.title_fr}</p>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-3">
            {renderFeatureValue(feature)}
            
            {/* Toggle switches for Active and New */}
            <div className="flex flex-col space-y-1">
              <label className="flex items-center gap-1 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={feature.is_active}
                    onChange={async (e) => {
                      try {
                        await apiService.updateFeature(feature.id, { ...feature, is_active: e.target.checked });
                        fetchFeatures(); // Refresh the features list
                        setToast({ message: `Feature ${e.target.checked ? 'activated' : 'deactivated'} successfully`, type: 'success' });
                      } catch (error) {
                        setToast({ message: `Failed to update feature: ${error.message}`, type: 'error' });
                      }
                    }}
                    className="sr-only"
                  />
                  <div className={`block w-8 h-4 rounded-full transition ${feature.is_active ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                  <div className={`dot absolute left-0.5 top-0.5 bg-white w-3 h-3 rounded-full transition ${feature.is_active ? 'transform translate-x-4' : ''}`}></div>
                </div>
                <span className="text-xs text-gray-600 dark:text-gray-400">Active</span>
              </label>
              
              <label className="flex items-center gap-1 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={feature.is_new}
                    onChange={async (e) => {
                      try {
                        await apiService.updateFeature(feature.id, { ...feature, is_new: e.target.checked });
                        fetchFeatures(); // Refresh the features list
                        setToast({ message: `NEW badge ${e.target.checked ? 'enabled' : 'disabled'} successfully`, type: 'success' });
                      } catch (error) {
                        setToast({ message: `Failed to update feature: ${error.message}`, type: 'error' });
                      }
                    }}
                    className="sr-only"
                  />
                  <div className={`block w-8 h-4 rounded-full transition ${feature.is_new ? 'bg-green-600' : 'bg-gray-300'}`}></div>
                  <div className={`dot absolute left-0.5 top-0.5 bg-white w-3 h-3 rounded-full transition ${feature.is_new ? 'transform translate-x-4' : ''}`}></div>
                </div>
                <span className="text-xs text-gray-600 dark:text-gray-400">NEW</span>
              </label>
            </div>
            
            {editingFeature === feature.id ? (
              <div className="flex space-x-1">
                <button
                  type="button"
                  onClick={() => handleSaveFeature(feature.id)}
                  className="rounded-lg bg-green-500 p-2 text-white hover:bg-green-600"
                >
                  <FiSave className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="rounded-lg bg-gray-300 p-2 text-gray-700 hover:bg-gray-400 dark:bg-navy-600 dark:text-gray-300"
                >
                  <FiX className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex space-x-1">
                <button
                  type="button"
                  onClick={() => handleEditFeature(feature)}
                  className="rounded-lg bg-blue-100 p-2 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400"
                >
                  <FiEdit2 className="h-4 w-4" />
                </button>
                {canDeleteFeature(feature) && (
                  <button
                    type="button"
                    onClick={() => handleDeleteFeature(feature)}
                    className="rounded-lg bg-red-100 p-2 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400"
                    title="Delete feature"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="rounded-2xl bg-white p-6 shadow-2xl dark:bg-navy-800" style={{ maxWidth: '400px', width: '90%' }}>
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                <FiTrash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-navy-700 dark:text-white">Delete Feature</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
              Are you sure you want to delete <span className="font-semibold text-navy-700 dark:text-white">"{deleteConfirm.featureName}"</span>?
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteFeature}
                disabled={loading}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default DraggableFeatureList;
